// deps
import { useCallback, useContext, useRef } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import {
    AnalyticsContext,
    ObjectNames,
    QrCodeActions,
    ResponseContext,
    ScreenNames,
    SwiggyContextHeader,
    TableContext,
    useDigitalMenuConfig,
    useDigitalMenuTheme,
    usePlApiContextFetch,
} from "@swiggy-private/portal-dineout-context";
import { WebStorageLocation } from "types";
import { useConfigTheme } from "./useConfigTheme";
import { useIsV2InApp } from "./useIsV2InApp";

// constants
import {
    API_ENDPOINTS,
    CART_NATIVE_BRIDGE_FLOW_TYPE,
    GANDALF_API_HOST,
    LOG_TABLES,
    STORAGE_KEYS,
    CART_LABELS,
    NR_TABLES,
} from "../constants";

// utils
import {
    getDefaultApiHeaders,
    getBridgeSdk,
    updateCartInfoWithTooltipConfig,
    saveToStorage,
    getFromStorage,
} from "../utils";
import { sendCustomNREvViaBlaze } from "../utils/newrelic";

export const useFetchCart = () => {
    const fetchPL = usePlApiContextFetch();
    const sdk = getBridgeSdk();
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { state, dispatch } = useContext(ResponseContext);

    const { restaurantId = "", tableId = "", parentOrderId = "" } = useContext(TableContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const cartImpressionRef = useRef<boolean>(false);
    const { isV2, isV2InApp } = useIsV2InApp();
    const theme = useConfigTheme();
    const { isConfigLoaded } = useDigitalMenuConfig();
    const { isThemeUpdating } = useDigitalMenuTheme();

    const onImpressionCartPage = useCallback(
        (isError = false) => {
            if (cartImpressionRef.current) return;

            if (impressionEvent) {
                impressionEvent({
                    category: ScreenNames.CART,
                    action: ObjectNames.IMPRESSION_CART_PAGE,
                    label: isError ? CART_LABELS.ERROR : CART_LABELS.SUCCESS,
                    context: JSON.stringify({
                        restaurant_id: restaurantId,
                        table_id: tableId,
                        parentOrderId,
                        table_no: state?.pin?.data?.tableNo,
                        is_v2_config: isV2,
                        theme,
                    }),
                });
                cartImpressionRef.current = true;
            }
        },
        [impressionEvent, restaurantId, tableId, parentOrderId, state?.pin?.data?.tableNo, isV2, theme],
    );

    const fetchCart = useCallback(async () => {
        // Guard: For V2 in app, block cart API calls during theme updates
        // Theme updates cause component remounts which trigger duplicate API calls
        // isThemeUpdating comes from ThemeContext and is set synchronously during render
        if (isV2InApp && (isThemeUpdating || !isConfigLoaded)) {
            return;
        }

        if (state.cart?.loading) {
            return;
        }

        if (!swiggyHeaders || !restaurantId || !fetchPL) {
            return;
        }

        dispatch({
            type: QrCodeActions.CART_SCREEN_LOADING,
            payload: {},
        });

        let finalLocalCart = {};

        // For V2 in app, fetch local cart from native bridge first
        if (isV2InApp) {
            try {
                const nativeCart = await sdk.dineoutGetLocalCart({ flowType: CART_NATIVE_BRIDGE_FLOW_TYPE });

                if (nativeCart) {
                    saveToStorage(STORAGE_KEYS.LOCAL_CART, JSON.stringify(nativeCart), WebStorageLocation.SESSION);
                    finalLocalCart = nativeCart;
                }
            } catch {
                sendCustomNREvViaBlaze(NR_TABLES.NATIVE_BRIDGE_FETCH_CART_ERROR, {}, swiggyHeaders ?? {});
            }
        }

        // If no cart from native bridge, try sessionStorage
        if (isEmpty(finalLocalCart)) {
            try {
                const storageResponse = await getFromStorage(STORAGE_KEYS.LOCAL_CART, WebStorageLocation.SESSION, true);
                const localCartString = storageResponse?.data?.[STORAGE_KEYS.LOCAL_CART] || "{}";
                const localCart = JSON.parse(localCartString);
                finalLocalCart = localCart || {};
            } catch (_) {
                sendCustomNREvViaBlaze(NR_TABLES.SESSION_STORAGE_FETCH_CART_ERROR, {}, swiggyHeaders ?? {});
            }
        }

        const hasNoCartItems =
            isEmpty(finalLocalCart) || isEmpty((finalLocalCart as { cartItems?: unknown[] })?.cartItems);

        if (hasNoCartItems) {
            dispatch({
                type: QrCodeActions.CART_SCREEN_ERROR,
                payload: {},
            });
            return;
        }
        try {
            const data = await fetchPL?.<{ cards: IPLWidgetControllerCards[] }>(
                {
                    apiHost: GANDALF_API_HOST,
                    endpoint: API_ENDPOINTS.UPDATE_CART,
                    retryCount: 3,
                    bodyParams: { ...finalLocalCart, restaurantId: Number(restaurantId) },
                    options: {
                        method: "POST",
                        headers: { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders) } as HeadersInit,
                    },
                },
                { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders) },
                () => {},
                LOG_TABLES,
            );

            if (!isEmpty(data) && data?.success) {
                const updatedCards = await updateCartInfoWithTooltipConfig(data.success.cards || []);
                const updatedData = { ...data, success: { ...data.success, cards: updatedCards } };

                onImpressionCartPage(false);
                dispatch({
                    type: QrCodeActions.CART_SCREEN_SUCCESS,
                    payload: { data: updatedData },
                });

                dispatch({
                    type: QrCodeActions.UPDATE_LOCAL_CART,
                    payload: finalLocalCart,
                });
            } else {
                // Handle empty response or unsuccessful API call
                dispatch({
                    type: QrCodeActions.CART_SCREEN_ERROR,
                    payload: {},
                });
            }
        } catch {
            onImpressionCartPage(true);
            dispatch({
                type: QrCodeActions.CART_SCREEN_ERROR,
                payload: {},
            });
        }
    }, [
        dispatch,
        fetchPL,
        onImpressionCartPage,
        isV2InApp,
        restaurantId,
        sdk,
        swiggyHeaders,
        state.cart?.loading,
        isConfigLoaded,
        isThemeUpdating,
    ]);

    return { fetchCart };
};
